import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Upload, Alert, Typography, Card, Radio, Space, Divider, message } from 'antd';
import { UploadOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import * as XLSX from 'xlsx';

const { Text, Title } = Typography;

interface SimilarityResult {
    row: number;
    function_name: string;
    task_name: string;
    suggestions: {
        function_suggestions: Array<{
            original: string;
            suggestions: string[];
        }>;
        task_suggestions: Array<{
            original: string;
            suggestions: string[];
        }>;
    };
}

interface TrainingResourceUploadModalProps {
    visible: boolean;
    onClose: () => void;
    testId: string | number;
    industryId: number;
    industryName?: string;
    isTemporaryTest: boolean;
    onUploadSuccess: (result: any) => void;
}

const TrainingResourceUploadModal: React.FC<TrainingResourceUploadModalProps> = ({
    visible,
    onClose,
    testId,
    industryId,
    industryName = '',
    isTemporaryTest,
    onUploadSuccess
}) => {
    const navigate = useNavigate();
    const [csvData, setCsvData] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState<any>(null);
    const [fileName, setFileName] = useState('');
    const [validating, setValidating] = useState(false);
    const [similarityResults, setSimilarityResults] = useState<SimilarityResult[]>([]);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [confirmedMappings, setConfirmedMappings] = useState<Record<string, string>>({});
    const [missingSelections, setMissingSelections] = useState<Set<string>>(new Set());
    const [showCsvPreview, setShowCsvPreview] = useState(false);
    const [conversionError, setConversionError] = useState<string | null>(null);
    const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

    // Reset modal state when it opens, but preserve uploadResult if it exists
    useEffect(() => {
        if (visible && !uploadResult) {
            // Only reset if there's no existing upload result to show
            setCsvData('');
            setFileName('');
            setSimilarityResults([]);
            setShowConfirmation(false);
            setConfirmedMappings({});
            setValidating(false);
            setUploading(false);
            setConversionError(null);
        }
    }, [visible, uploadResult]);

    const convertExcelToDelimitedText = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const arrayBuffer = e.target?.result as ArrayBuffer;
                    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                    if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
                        reject(new Error('No worksheet found in the Excel file.'));
                        return;
                    }

                    const firstSheetName = workbook.SheetNames[0];
                    const firstSheet = workbook.Sheets[firstSheetName];
                    if (!firstSheet) {
                        reject(new Error('Unable to read the first worksheet.'));
                        return;
                    }

                    const text = XLSX.utils.sheet_to_csv(firstSheet, {
                        FS: ',',
                        RS: '\n',
                        blankrows: false,
                    });

                    if (!text || !text.trim()) {
                        reject(new Error('The first worksheet is empty.'));
                        return;
                    }

                    resolve(text);
                } catch {
                    reject(new Error('Could not convert this Excel file. Please check the file format and try again.'));
                }
            };
            reader.onerror = () => reject(new Error('Failed to read the Excel file.'));
            reader.readAsArrayBuffer(file);
        });
    };

    const handleFileSelect = async (file: File) => {
        setConversionError(null);

        const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
        const isExcel = fileExt === 'xls' || fileExt === 'xlsx';

        if (isExcel) {
            try {
                const convertedText = await convertExcelToDelimitedText(file);
                setCsvData(convertedText);
                setFileName(file.name);
                message.success('Excel file converted successfully. Click "Process Resources" to continue.');
            } catch (err: any) {
                setCsvData('');
                setFileName(file.name);
                setConversionError(err?.message || 'Excel conversion failed. Please use CSV or a valid Excel file.');
            }
            return false;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            setCsvData(e.target?.result as string);
            setFileName(file.name);
        };
        reader.onerror = () => {
            setCsvData('');
            setFileName(file.name);
            setConversionError('Failed to read the selected file. Please try again.');
        };
        reader.readAsText(file);
        return false; // Prevent auto upload
    };

    const handleUpload = async () => {
        if (!csvData) return;

        // First, validate the CSV and check for similar names
        setValidating(true);

        try {
            const validationResponse = await api.post('/process-training-resources/', {
                industry_id: industryId,
                csv_data: csvData,
                validate_only: true
            });

            if (validationResponse.data.success) {
                if (validationResponse.data.requires_confirmation) {
                    // Show confirmation modal for similar names
                    setSimilarityResults(validationResponse.data.similarity_suggestions);
                    setShowConfirmation(true);
                    setValidating(false);
                } else {
                    // No conflicts, proceed with upload
                    await processUpload({});
                }
            } else {
                setUploadResult(validationResponse.data);
                setValidating(false);
            }
        } catch (error: any) {
            console.error('Validation failed:', error);

            // Handle header validation errors specifically
            if (error.response?.data?.header_errors) {
                const headerData = error.response.data;
                setUploadResult({
                    success: false,
                    error: 'CSV Format Error',
                    header_errors: headerData.header_errors,
                    missing_required: headerData.missing_required,
                    suggestions: headerData.suggestions,
                    expected_columns: headerData.expected_columns,
                    required_columns: headerData.required_columns
                });
            } else {
                setUploadResult({
                    success: false,
                    error: 'Validation failed. Please try again.'
                });
            }
            setValidating(false);
        }
    };

    const processUpload = async (mappings: Record<string, string>) => {
        setUploading(true);

        try {
            const response = await api.post('/process-training-resources/', {
                industry_id: industryId,
                test_id: isTemporaryTest ? undefined : testId,
                csv_data: csvData,
                confirmed_mappings: mappings
            });

            setUploadResult(response.data);
            if (response.data.success) {
                // Reset all states before calling success callback
                setValidating(false);
                setUploading(false);
                setShowConfirmation(false);
                onUploadSuccess(response.data);
            }
        } catch (error) {
            console.error('Upload failed:', error);
            setUploadResult({
                success: false,
                error: 'Upload failed. Please try again.'
            });
        } finally {
            setUploading(false);
        }
    };

    const handleConfirmMappings = () => {
        // Get all required mapping keys
        const requiredKeys: string[] = [];
        similarityResults.forEach((result) => {
            result.suggestions.function_suggestions.forEach((funcSugg) => {
                requiredKeys.push(`function_${result.row}_${funcSugg.original}`);
            });
            result.suggestions.task_suggestions.forEach((taskSugg) => {
                requiredKeys.push(`task_${result.row}_${taskSugg.original}`);
            });
        });

        // Check for missing selections
        const missing = requiredKeys.filter(key => !confirmedMappings[key]);

        if (missing.length > 0) {
            // Update missing selections state
            setMissingSelections(new Set(missing));

            // Show error message
            message.error(`Please make selections for all ${missing.length} conflict${missing.length > 1 ? 's' : ''} before proceeding.`);

            // Scroll to first missing selection
            const firstMissingKey = missing[0];
            const cardId = getCardIdFromKey(firstMissingKey);
            if (cardRefs.current[cardId]) {
                cardRefs.current[cardId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }

            return;
        }

        // Clear missing selections and proceed
        setMissingSelections(new Set());
        processUpload(confirmedMappings);
    };

    const getCardIdFromKey = (key: string): string => {
        // Extract row number from key like "function_2_Something" or "task_4_Something"
        const match = key.match(/^(function|task)_(\d+)_/);
        return match ? `card_${match[2]}` : '';
    };

    const handleMappingChange = (key: string, value: string) => {
        setConfirmedMappings(prev => ({
            ...prev,
            [key]: value
        }));

        // Remove from missing selections when user makes a choice
        setMissingSelections(prev => {
            const newSet = new Set(prev);
            newSet.delete(key);
            return newSet;
        });
    };

    const handleReset = () => {
        setCsvData('');
        setUploadResult(null);
        setFileName('');
        setSimilarityResults([]);
        setShowConfirmation(false);
        setConfirmedMappings({});
        setValidating(false);
        setConversionError(null);
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const getPreviewData = () => {
        if (!csvData) return '';
        const lines = csvData.split('\n');
        const previewLines = lines.slice(0, 5);
        if (lines.length > 5) {
            previewLines.push('...');
        }
        return previewLines.join('\n');
    };

    const downloadSampleCSV = () => {
        const headers = [
            'function_name',
            'task',
            'title',
            'url_1',
            'url_2',
            'requires_login',
            'resource_type',
            'state_specific',
            'fallback_content',
            'priority',
            'notes',
            'cost'
        ];

        const sampleRows = [
            [
                'Payroll',
                'STP Finalisation',
                'STP Guide',
                'https://help.xero.com/au/payroll-stp',
                '',
                'false',
                'vendor',
                '',
                'Manual process for STP finalisation',
                '1',
                'Additional notes about STP',
                'Free'
            ],
            [
                'GST',
                'GST Calculations',
                'GST Calculator Tool',
                'https://example.com/gst-tool',
                'https://example.com/gst-backup',
                'true',
                'expert',
                'NSW',
                'Alternative GST calculation method',
                '1',
                'Premium GST calculation tool',
                '$29/month'
            ]
        ];

        const csvContent = [headers, ...sampleRows]
            .map(row => row.map(field => `"${field}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'training_resources_sample.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <>
            <Modal
                title="Upload Training Resources"
                open={visible}
                onCancel={handleClose}
                footer={[
                    <Button
                        key="reset"
                        onClick={handleReset}
                        disabled={uploading || validating}
                        style={{
                            borderRadius: 9999,
                            padding: "8px 20px",
                            fontWeight: 600,
                            height: '40px'
                        }}
                    >
                        Reset
                    </Button>,
                    <Button
                        key="cancel"
                        onClick={handleClose}
                        disabled={uploading || validating}
                        style={{
                            borderRadius: 9999,
                            padding: "8px 20px",
                            fontWeight: 600,
                            height: '40px'
                        }}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="process"
                        type="primary"
                        loading={uploading || validating}
                        disabled={!csvData || (uploadResult && uploadResult.success)}
                        onClick={handleUpload}
                    >
                        {uploadResult && uploadResult.success
                            ? 'Already Processed'
                            : validating ? 'Validating...'
                                : uploading ? 'Uploading...'
                                    : 'Process Resources'
                        }
                    </Button>
                ]}
                width={700}
            >
                <div className="upload-content">
                    <div style={{ marginBottom: 16 }}>
                        <Text>Upload CSV or Excel data to create training resources for this test.</Text>
                        {industryName && (
                            <div style={{
                                marginTop: 8,
                                padding: '8px 12px',
                                background: '#f0f8ff',
                                border: '1px solid #d1ecf1',
                                borderRadius: 4
                            }}>
                                <Text style={{ fontSize: '13px', color: '#0c5460' }}>
                                    <strong>Industry:</strong> {industryName}
                                </Text>
                            </div>
                        )}
                    </div>                <Upload
                        accept=".csv,.tsv,.txt,.xls,.xlsx"
                        beforeUpload={handleFileSelect}
                        showUploadList={false}
                        disabled={uploadResult && uploadResult.success}
                    >
                        <Button
                            icon={<UploadOutlined />}
                            disabled={uploadResult && uploadResult.success}
                        >
                            {uploadResult && uploadResult.success ? 'File Already Processed' : 'Select CSV or Excel File'}
                        </Button>
                    </Upload>

                    {conversionError && (
                        <Alert
                            type="error"
                            showIcon
                            message="File Conversion Error"
                            style={{ marginTop: 16 }}
                            description={
                                <div>
                                    <div>{conversionError}</div>
                                    <div style={{ marginTop: 8 }}>
                                        Please export the spreadsheet as CSV and upload again if the issue persists.
                                    </div>
                                </div>
                            }
                        />
                    )}

                    {fileName && (
                        <div style={{ marginTop: 8 }}>
                            <Text>Selected: {fileName}</Text>
                        </div>
                    )}

                    {csvData && (
                        <div style={{ marginTop: 16 }}>
                            <div
                                style={{
                                    padding: '8px 12px',
                                    background: '#f8f9fa',
                                    border: '1px solid #e9ecef',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                                onClick={() => setShowCsvPreview(true)}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#e9ecef'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#f8f9fa'}
                            >
                                <Text style={{ color: '#495057' }}>
                                    📄 <strong>View CSV Data Preview</strong> ({csvData.split('\n').length - 1} rows detected)
                                </Text>
                            </div>
                        </div>
                    )}

                    {uploadResult && (
                        <Alert
                            type={uploadResult.success ? "success" : "error"}
                            message={uploadResult.success ?
                                "Upload completed successfully!" :
                                uploadResult.error || "Upload failed"
                            }
                            description={
                                uploadResult.header_errors ? (
                                    <div>
                                        <div style={{ marginBottom: 12 }}>
                                            <strong>Your CSV file has incorrect column headers.</strong>
                                        </div>

                                        {uploadResult.missing_required && uploadResult.missing_required.length > 0 && (
                                            <div style={{ marginBottom: 8 }}>
                                                • Missing required columns: <strong>{uploadResult.missing_required.join(', ')}</strong>
                                            </div>
                                        )}

                                        {Object.keys(uploadResult.suggestions || {}).length > 0 && (
                                            <div style={{ marginBottom: 8 }}>
                                                • Incorrect column names found:
                                                {Object.entries(uploadResult.suggestions || {}).map(([wrong, correct]: [string, any]) => (
                                                    <div key={wrong} style={{ marginLeft: 16 }}>
                                                        Change "{wrong}" → "{correct}"
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div style={{
                                            marginTop: 16,
                                            padding: 12,
                                            background: '#e6f7ff',
                                            border: '1px solid #91d5ff',
                                            borderRadius: 4
                                        }}>
                                            <strong>💡 Tip:</strong> Download the sample CSV template below and use it as a starting point to avoid this error.
                                        </div>
                                    </div>
                                ) : uploadResult.errors && uploadResult.errors.length > 0 ?
                                    `${uploadResult.errors.length} errors encountered: ${uploadResult.errors.slice(0, 3).join(', ')}${uploadResult.errors.length > 3 ? '...' : ''}` :
                                    uploadResult.success ? (
                                        <div>
                                            <div style={{ marginBottom: 8 }}>
                                                <strong>Summary:</strong>
                                            </div>
                                            <div>
                                                • <strong>{uploadResult.created_functions || 0}</strong> new functions created
                                            </div>
                                            <div>
                                                • <strong>{uploadResult.created_tasks || 0}</strong> new tasks created
                                            </div>
                                            <div>
                                                • <strong>{uploadResult.created_resources || 0}</strong> new resources created
                                            </div>
                                            <div>
                                                • <strong>{uploadResult.updated_resources || 0}</strong> resources updated (URLs, notes, etc.)
                                            </div>
                                        </div>
                                    ) : null
                            }
                            style={{ marginTop: 16 }}
                            showIcon
                        />
                    )}

                    <div style={{ marginTop: 16, padding: 12, background: '#f0f0f0', borderRadius: 4 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                            <div style={{ flex: 1 }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    <strong>Required CSV columns:</strong> function_name, task, title, url_1, url_2, requires_login, resource_type, state_specific, fallback_content, priority, notes, cost
                                </Text>
                            </div>
                            <Button
                                size="small"
                                onClick={downloadSampleCSV}
                                style={{
                                    borderRadius: 6,
                                    fontSize: '11px',
                                    height: '28px',
                                    marginLeft: '12px'
                                }}
                            >
                                Download Sample CSV
                            </Button>
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <strong>Example:</strong> "Payroll","STP Finalisation","STP Guide","https://..","",false,"vendor","","Manual process...",1,"Additional notes","Free"
                        </Text>
                    </div>
                </div>
            </Modal>

            {/* Similarity Confirmation Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                        <span>Similar Names Found</span>
                    </div>
                }
                open={showConfirmation}
                onCancel={() => setShowConfirmation(false)}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => setShowConfirmation(false)}
                        style={{
                            borderRadius: 9999,
                            padding: "8px 20px",
                            fontWeight: 600,
                            height: '40px'
                        }}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="confirm"
                        type="primary"
                        loading={uploading}
                        onClick={handleConfirmMappings}
                    >
                        Confirm and Upload
                    </Button>
                ]}
                width={800}
            >
                <div style={{ marginBottom: 16 }}>
                    <Alert
                        type="warning"
                        message="Similar function or task names were found in your CSV. Please choose whether to use existing names or create new ones."
                        showIcon
                    />
                </div>

                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {similarityResults.map((result, index) => {
                        const cardId = `card_${result.row}`;

                        // Check if this card has any missing selections
                        const cardHasMissing = similarityResults.some(r => {
                            if (r.row !== result.row) return false;
                            return [
                                ...r.suggestions.function_suggestions.map(fs => `function_${r.row}_${fs.original}`),
                                ...r.suggestions.task_suggestions.map(ts => `task_${r.row}_${ts.original}`)
                            ].some(key => missingSelections.has(key));
                        });

                        return (
                            <Card
                                key={index}
                                size="small"
                                style={{
                                    marginBottom: 16,
                                    border: cardHasMissing ? '2px solid #ff4d4f' : undefined,
                                    boxShadow: cardHasMissing ? '0 0 8px rgba(255, 77, 79, 0.3)' : undefined,
                                    transition: 'all 0.3s ease'
                                }}
                                ref={(el) => { cardRefs.current[cardId] = el; }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Title level={5} style={{ margin: 0 }}>Row {result.row}</Title>
                                    {cardHasMissing && (
                                        <Alert
                                            type="error"
                                            message="Selection required"
                                            showIcon
                                            style={{
                                                marginLeft: 'auto',
                                                fontSize: '12px',
                                                padding: '4px 8px',
                                                minHeight: 'auto'
                                            }}
                                        />
                                    )}
                                </div>

                                {/* Function suggestions */}
                                {result.suggestions.function_suggestions.map((funcSugg, funcIndex) => (
                                    <div key={funcIndex} style={{ marginBottom: 16 }}>
                                        <Text strong>Function: "{funcSugg.original}"</Text>
                                        <div style={{ marginTop: 8, paddingLeft: 16 }}>
                                            <Radio.Group
                                                value={confirmedMappings[`function_${result.row}_${funcSugg.original}`] || undefined}
                                                onChange={(e) => handleMappingChange(`function_${result.row}_${funcSugg.original}`, e.target.value)}
                                                style={{
                                                    border: missingSelections.has(`function_${result.row}_${funcSugg.original}`) ? '1px solid #ff4d4f' : undefined,
                                                    borderRadius: missingSelections.has(`function_${result.row}_${funcSugg.original}`) ? '4px' : undefined,
                                                    padding: missingSelections.has(`function_${result.row}_${funcSugg.original}`) ? '8px' : undefined,
                                                    backgroundColor: missingSelections.has(`function_${result.row}_${funcSugg.original}`) ? '#fff2f0' : undefined
                                                }}
                                            >
                                                <Space direction="vertical">
                                                    <Radio value={funcSugg.original}>
                                                        Create new: "{funcSugg.original}"
                                                    </Radio>
                                                    {funcSugg.suggestions.map((suggestion, suggIndex) => (
                                                        <Radio key={suggIndex} value={suggestion}>
                                                            Use existing: "{suggestion}"
                                                        </Radio>
                                                    ))}
                                                </Space>
                                            </Radio.Group>
                                        </div>
                                    </div>
                                ))}

                                {/* Task suggestions */}
                                {result.suggestions.task_suggestions.map((taskSugg, taskIndex) => (
                                    <div key={taskIndex}>
                                        <Text strong>Task: "{taskSugg.original}"</Text>
                                        <div style={{ marginTop: 8, paddingLeft: 16 }}>
                                            <Radio.Group
                                                value={confirmedMappings[`task_${result.row}_${taskSugg.original}`] || undefined}
                                                onChange={(e) => handleMappingChange(`task_${result.row}_${taskSugg.original}`, e.target.value)}
                                                style={{
                                                    border: missingSelections.has(`task_${result.row}_${taskSugg.original}`) ? '1px solid #ff4d4f' : undefined,
                                                    borderRadius: missingSelections.has(`task_${result.row}_${taskSugg.original}`) ? '4px' : undefined,
                                                    padding: missingSelections.has(`task_${result.row}_${taskSugg.original}`) ? '8px' : undefined,
                                                    backgroundColor: missingSelections.has(`task_${result.row}_${taskSugg.original}`) ? '#fff2f0' : undefined
                                                }}
                                            >
                                                <Space direction="vertical">
                                                    <Radio value={taskSugg.original}>
                                                        Create new: "{taskSugg.original}"
                                                    </Radio>
                                                    {taskSugg.suggestions.map((suggestion, suggIndex) => (
                                                        <Radio key={suggIndex} value={suggestion}>
                                                            Use existing: "{suggestion}"
                                                        </Radio>
                                                    ))}
                                                </Space>
                                            </Radio.Group>
                                        </div>
                                    </div>
                                ))}
                            </Card>
                        );
                    })}
                </div>
            </Modal>

            {/* CSV Preview Modal */}
            <Modal
                title="CSV Data Preview"
                open={showCsvPreview}
                onCancel={() => setShowCsvPreview(false)}
                footer={[
                    <Button
                        key="close"
                        onClick={() => setShowCsvPreview(false)}
                        style={{
                            borderRadius: 9999,
                            padding: "8px 20px",
                            fontWeight: 600,
                            height: '40px'
                        }}
                    >
                        Close
                    </Button>
                ]}
                width={800}
            >
                <div style={{
                    background: '#f5f5f5',
                    padding: 12,
                    borderRadius: 4,
                    maxHeight: 500,
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap'
                }}>
                    {getPreviewData()}
                </div>
                <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {csvData ? csvData.split('\n').length - 1 : 0} rows detected
                    </Text>
                </div>
            </Modal>
        </>
    );
};

export default TrainingResourceUploadModal;